import { UseFormReturnType } from "@mantine/form"
import {
  CountrySelector,
  LabelWithTooltip,
  MultiSelect,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "@tfg/ui"

import { SelectListRegistrationIdField } from "~/components/SelectListRegistrationIdField"
import { ProductionSiteRepositoryAutocompleteField } from "~/pages/SupplierPortal/ProductionSites/components/ProductionSiteRepositoryAutocompleteField"
import { COUNTRIES } from "~/utils/countries"

import type { SchemaFieldRenderer } from "./schema-form.types"

interface BaseProps {
  readonly name: string
  readonly label: string
  readonly tooltip: string | undefined
  readonly isRequired: boolean
  readonly placeholder: string | undefined
  readonly disabled: boolean | undefined
  readonly error: string | undefined
  readonly form: UseFormReturnType<Record<string, unknown>>
}

const PRODUCTION_SITE_LINKED_READ_ONLY_FIELDS = new Set<string>([
  "parentCompany",
  "internalCode",
  "performedActivities",
  "registrationIds",
  "street",
  "city",
  "postalCode",
  "longitude",
  "latitude",
  "countryIso3166",
])

const isLinkedRepositoryRecord = (value: unknown): boolean => {
  return typeof value === "string" && value.trim().length > 0
}

const resolveFieldDisabledState = ({
  name,
  disabled,
  form,
}: {
  readonly name: string
  readonly disabled: boolean | undefined
  readonly form: UseFormReturnType<Record<string, unknown>>
}): boolean => {
  if (disabled === true) {
    return true
  }

  const repositoryRecordId = form.values.repositoryRecordId
  const isInLinkedMode = isLinkedRepositoryRecord(repositoryRecordId)

  return isInLinkedMode && PRODUCTION_SITE_LINKED_READ_ONLY_FIELDS.has(name)
}

const HiddenFieldRenderer: SchemaFieldRenderer = () => null

interface TextFieldRendererProps extends BaseProps {}

const TextFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  placeholder,
  disabled,
  isRequired,
  error,
  form,
}: TextFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <TextInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
          justify="space-between"
        />
      }
      labelProps={{
        w: "100%",
      }}
      w="100%"
      placeholder={placeholder}
      disabled={fieldDisabled}
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(event) => {
        form.setFieldValue(name, event.currentTarget.value)
        form.validateField(name)
      }}
    />
  )
}

interface NumberFieldRendererProps extends BaseProps {}

const NumberFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  disabled,
  error,
  form,
}: NumberFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <NumberInput
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      disabled={fieldDisabled}
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        // need to validate the whole form because
        // latitude and longitude are valdiated together in super rafine
        form.validate()
      }}
    />
  )
}

interface BooleanFieldRendererProps extends BaseProps {}

const BooleanFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  disabled,
  error,
  form,
}: BooleanFieldRendererProps) => {
  const inputProps = form.getInputProps(name, { type: "checkbox" })
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <Switch
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      disabled={fieldDisabled}
      {...inputProps}
      error={error ?? inputProps.error}
    />
  )
}

interface SelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
}

const SelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  disabled,
  options,
  error,
  form,
}: SelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <Select
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      placeholder={placeholder}
      disabled={fieldDisabled}
      searchable
      clearable
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      {...inputProps}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        form.validateField(name)
      }}
    />
  )
}

interface MultiSelectFieldRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
      }>
    | undefined
}

const MultiSelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  isRequired,
  tooltip,
  placeholder,
  disabled,
  options,
  error,
  form,
}: MultiSelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const value = (inputProps.value as string[] | undefined) ?? []
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <MultiSelect
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
          w="100%"
        />
      }
      placeholder={placeholder}
      disabled={fieldDisabled}
      searchable
      size="xs"
      clearable
      labelProps={{
        w: "100%",
      }}
      data={
        options?.map((opt) => ({ label: opt.label, value: opt.value })) ?? []
      }
      value={value}
      error={error ?? inputProps.error}
      onChange={(values) => {
        form.setFieldValue(name, values)
        form.validateField(name)
      }}
    />
  )
}

interface SelectListRegistrationIdRendererProps extends BaseProps {
  readonly options:
    | ReadonlyArray<{
        readonly label: string
        readonly value: string
        readonly description?: string
      }>
    | undefined
}

const SelectListRegistrationIdRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  placeholder,
  disabled,
  options,
  error: fieldError,
  form,
}: SelectListRegistrationIdRendererProps) => {
  const value =
    (form.values[name] as
      | { id?: string; registrationId: string; type: string }[]
      | undefined) ?? []
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })

  return (
    <SelectListRegistrationIdField
      value={value}
      onChange={(list) => {
        form.setFieldValue(name, list)
        form.validateField(name)
      }}
      onValidate={() => form.validateField(name)}
      options={options?.map((o) => ({ ...o })) ?? []}
      label={label}
      tooltip={tooltip}
      isRequired={isRequired}
      placeholder={placeholder}
      disabled={fieldDisabled}
      fieldError={fieldError}
    />
  )
}

interface CountrySelectFieldRendererProps extends BaseProps {}

const CountrySelectFieldRenderer: SchemaFieldRenderer = ({
  name,
  label,
  tooltip,
  isRequired,
  disabled,
  placeholder,
  error,
  form,
}: CountrySelectFieldRendererProps) => {
  const inputProps = form.getInputProps(name)
  const fieldDisabled = resolveFieldDisabledState({ name, disabled, form })
  return (
    <CountrySelector
      label={
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          withAsterisk={isRequired}
        />
      }
      labelProps={{
        w: "100%",
      }}
      searchable
      placeholder={placeholder}
      disabled={fieldDisabled}
      countriesMapper={COUNTRIES}
      {...inputProps}
      value={inputProps.value ?? null}
      error={error ?? inputProps.error}
      onChange={(value) => {
        form.setFieldValue(name, value)
        form.validateField(name)
      }}
    />
  )
}

export const DEFAULT_FIELD_RENDERERS: Record<string, SchemaFieldRenderer> = {
  text: TextFieldRenderer,
  number: NumberFieldRenderer,
  boolean: BooleanFieldRenderer,
  select: SelectFieldRenderer,
  multiSelect: MultiSelectFieldRenderer,
  selectListRegistrationId: SelectListRegistrationIdRenderer,
  countrySelect: CountrySelectFieldRenderer,
  productionSiteRepositoryAutocomplete:
    ProductionSiteRepositoryAutocompleteField,
  hidden: HiddenFieldRenderer,
}
